import { Typography } from '@ag1/ui';
import { useProductStore } from '@portal/state/store/pdpStore';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Variant } from 'state/store/types';
import {
  QuantitySelectorCards as QuantitySelectorCardsProps,
  SanityKeyed
} from 'types/studio/studio';
import { CommonSectionProps } from '../../types';
import { getUniqueVariantId } from '../utils';
import styles from './QuantitySelectorCards.module.scss';

const QuantitySelectorCards = (
  props: SanityKeyed<QuantitySelectorCardsProps> & CommonSectionProps
) => {
  const { _key: key, title, hideSavingsLabels, hidePricePerServing } = props;

  const router = useRouter();
  const variants = useProductStore((state) => state.variants);
  const activeVariantId = useProductStore((state) => state.selectedVariantId);
  const setActiveVariantId = useProductStore((state) => state.setSelectedVariantId);
  const isOtpProduct = useProductStore((state) => state.isOtpProduct);
  const flavor = useProductStore((state) => state.flavor);

  // Update the active variant ID whenever the URL query changes
  useEffect(() => {
    if (!variants || variants.length === 0 || !router.isReady) return;

    // Check if we have a quantity parameter that's numerical (e.g., 30, 60, 90)
    const quantityParam = router.query.quantity;

    if (quantityParam && !Number.isNaN(Number(quantityParam))) {
      // Find the variant with matching quantity
      const selectedQty = Number(quantityParam);
      const matchingVariant = variants.find((variant) => variant.quantity === selectedQty);

      if (matchingVariant) {
        setActiveVariantId(getUniqueVariantId(matchingVariant, flavor) || '');
        return;
      }
    }

    // Fallback to the old qty parameter for backward compatibility
    const qtyParam = router.query.qty;
    if (qtyParam) {
      const queryMatchVariant = variants.find(
        (variant) => getUniqueVariantId(variant, flavor) === qtyParam
      );

      if (queryMatchVariant) {
        setActiveVariantId(getUniqueVariantId(queryMatchVariant, flavor) || '');
        return;
      }
    }

    // Default to first variant if no match
    setActiveVariantId(getUniqueVariantId(variants[0], flavor) || '');
  }, [variants, router.query, router.isReady, flavor, setActiveVariantId]);

  if (!title || !variants?.length || !router.isReady) return null;

  // Handle radio change - update URL and state
  const handleRadioChange = (variant: Variant) => {
    const variantId = getUniqueVariantId(variant, flavor);

    // Update the product store
    setActiveVariantId(variantId);

    // Update URL without full page refresh
    const queryParams = { ...router.query };

    // Use the actual quantity value as the URL parameter
    queryParams.quantity = String(variant.quantity);

    if (flavor) {
      queryParams.flavor = flavor;
    }

    router.push(
      {
        pathname: router.pathname,
        query: queryParams
      },
      undefined,
      { shallow: true }
    );
  };

  // Card content component
  const renderCardContent = (variant: Variant) => (
    <>
      {variant.qtyCardBannerTitle && variant.qtyCardBannerColor && (
        <div
          className={clsx(styles.bannerContainer, {
            [styles.emphasisBanner]: variant.qtyCardBannerColor === 'emphasis',
            [styles.savingsBanner]: variant.qtyCardBannerColor === 'savings',
            [styles.primaryBanner]: variant.qtyCardBannerColor === 'primary'
          })}>
          <Typography variant="subtitleMono" size="2" noPadding className={styles.bannerTitle}>
            {variant.qtyCardBannerTitle || ''}
          </Typography>
        </div>
      )}
      <div className={styles.cardTitleContainer}>
        <span className={styles.cardTitle}>{variant.qtyCardTitle || ''}</span>
        {!hidePricePerServing && variant.qtyPricePerServing && (
          <div className={styles.pricePerServing}>
            {!isOtpProduct ? variant.qtyPricePerServing : variant.qtyPricePerServingOTP}
          </div>
        )}

        {!hideSavingsLabels && (
          <div className={styles.cardSubtitle}>
            {(!isOtpProduct && variant.qtyCardSubtitle) || ''}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={styles.qtyContainer} id={key}>
      <div className={styles.qtyLabel}>
        <Typography
          variant="subtitleMono"
          size="2"
          noPadding
          color="black-new"
          className={styles.qtyHeading}>
          {`${title}:`}
        </Typography>
      </div>
      <div className={styles.cardsContainer}>
        {variants.map((variant) => {
          if (!variant.subscriptionVariantIds[0]?.variantId || !variant.qtyCardTitle) return null;

          const isSelected = activeVariantId === getUniqueVariantId(variant, flavor);
          const isDisabled = isOtpProduct && variants.indexOf(variant) !== 0;
          const variantId = getUniqueVariantId(variant, flavor);
          const inputId = `qty-${variantId}`;

          // Common CSS classes applied to all cards
          const cardClasses = clsx(styles.card, { [styles.hideSavingsLabels]: hideSavingsLabels });

          return (
            <div key={variant._key} className={cardClasses}>
              <input
                id={inputId}
                type="radio"
                name="quantity-selector"
                value={variantId}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => !isDisabled && handleRadioChange(variant)}
                className={styles.hiddenRadio}
              />
              <label htmlFor={inputId} className={styles.cardLabel}>
                {renderCardContent(variant)}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuantitySelectorCards;
